import { http } from "../http";

// ── Types ──

export interface SocialPlatform {
    id: number;
    platform: string;
    account_name: string;
    is_active: boolean;
    platform_id?: string;
    token_expires?: string;
    metadata?: Record<string, any>;
    created_at: string;
    updated_at: string;
}

export interface SocialPost {
    id: number;
    campaign_id: number;
    platform_id: number;
    content: string;
    media_urls?: string[];
    post_type: string;
    status: string;
    platform_post_id?: string;
    platform_url?: string;
    scheduled_at?: string;
    posted_at?: string;
    error_message?: string;
    retry_count: number;
    impressions?: number;
    engagements?: number;
    clicks?: number;
    shares?: number;
    comments_count?: number;
    likes?: number;
    created_at: string;
    platform?: {
        id: number;
        platform: string;
        account_name: string;
    };
}

export interface ContentCampaign {
    id: number;
    title: string;
    source_type: string;
    source_id?: number;
    status: string;
    ai_prompt?: string;
    base_content?: string;
    metadata?: Record<string, any>;
    scheduled_at?: string;
    completed_at?: string;
    created_at: string;
    updated_at: string;
    posts?: SocialPost[];
    assets?: GeneratedAsset[];
    _count?: { posts: number; assets: number };
}

export interface GeneratedAsset {
    id: number;
    campaign_id?: number;
    asset_type: string;
    content: string;
    media_url?: string;
    prompt_used?: string;
    created_at: string;
}

export interface DashboardStats {
    connectedPlatforms: number;
    platforms: Array<{
        id: number;
        platform: string;
        accountName: string;
        tokenExpires?: string;
        tokenHealthy: boolean;
    }>;
    totalPosts: number;
    pendingPosts: number;
    postedPosts: number;
    failedPosts: number;
    expiringTokens: number;
}

export interface DashboardAnalytics {
    totals: {
        totalPosts: number;
        totalImpressions: number;
        totalEngagements: number;
        totalClicks: number;
        totalLikes: number;
        totalShares: number;
        engagementRate: string;
    };
    byPlatform: Record<
        string,
        {
            posts: number;
            impressions: number;
            engagements: number;
            clicks: number;
            likes: number;
            shares: number;
        }
    >;
    dailyTrend: Array<{
        date: string;
        posts: number;
        impressions: number;
        engagements: number;
    }>;
    topPosts: Array<{
        id: number;
        platform: string;
        content: string;
        impressions: number;
        engagements: number;
        likes: number;
        platformUrl: string;
        postedAt: string;
    }>;
}

export interface ContentBundle {
    variants: Array<{
        platform: string;
        content: string;
        postType: string;
    }>;
    imagePrompts: string[];
}

// ── Platform display info ──

export const PLATFORM_INFO: Record<
    string,
    { name: string; color: string; icon: string; setupSteps: string[] }
> = {
    twitter: {
        name: "X (Twitter)",
        color: "#000000",
        icon: "𝕏",
        setupSteps: [
            "Go to developer.twitter.com → Create a project & app",
            "Enable OAuth 2.0 with PKCE in User Auth Settings",
            "Set scopes: tweet.read, tweet.write, users.read, offline.access",
            "Get API Key, API Secret, and Bearer Token",
            "Use OAuth 2.0 flow to get user access_token + refresh_token",
            "Free tier: 1,500 tweets/month",
        ],
    },
    instagram: {
        name: "Instagram",
        color: "#E4405F",
        icon: "📸",
        setupSteps: [
            "Go to developers.facebook.com → Create Business App",
            'Add "Instagram Graph API" product',
            "Connect Instagram Business/Creator account to a Facebook Page",
            "Generate a long-lived Page Access Token (60 days)",
            "Permissions: instagram_basic, instagram_content_publish, pages_show_list",
            "Get Instagram Business Account ID via /me/accounts → page → instagram_business_account",
            'Store instagram_account_id in metadata field as: {"instagram_account_id":"..."}',
        ],
    },
    facebook: {
        name: "Facebook",
        color: "#1877F2",
        icon: "📘",
        setupSteps: [
            "Go to developers.facebook.com → Create Business App",
            'Add "Facebook Login for Business" product',
            "Permissions: pages_manage_posts, pages_read_engagement",
            "Generate Page Access Token (non-expiring when from long-lived user token)",
            'Store page_id in metadata: {"page_id":"..."}',
        ],
    },
    linkedin: {
        name: "LinkedIn",
        color: "#0A66C2",
        icon: "💼",
        setupSteps: [
            "Go to linkedin.com/developers → Create an App",
            "Associate with your LinkedIn Company Page",
            'Request "Community Management API" access',
            "OAuth scopes: w_member_social, w_organization_social, r_organization_social",
            "Get organization URN: urn:li:organization:{COMPANY_ID}",
            'Store in metadata: {"organization_urn":"urn:li:organization:12345"}',
            "Token expires in 60 days — refresh_token valid 365 days",
        ],
    },
    pinterest: {
        name: "Pinterest",
        color: "#E60023",
        icon: "📌",
        setupSteps: [
            "Go to developers.pinterest.com → Create an App",
            "Request API v5 access (auto-approved for business accounts)",
            "OAuth scopes: boards:read, boards:write, pins:read, pins:write",
            "Create a board for your content (e.g. 'Speech Therapy Tips')",
            'Store board_id in metadata: {"board_id":"...","default_link":"https://talaqah.app"}',
            "Free: 1,000 pins/day — each pin is an SEO backlink!",
        ],
    },
    google_business: {
        name: "Google Business Profile",
        color: "#4285F4",
        icon: "🗺️",
        setupSteps: [
            "Go to console.cloud.google.com → Enable My Business APIs",
            "Create OAuth 2.0 credentials (Web Application)",
            "Authorize with account that manages the GBP listing",
            "Scopes: openid, googleapis.com/auth/business.manage",
            "Get location: accounts/{id}/locations/{id}",
            'Store in metadata: {"location_name":"accounts/123/locations/456","cta_url":"https://talaqah.app"}',
            "Massive local SEO impact — posts appear in Google Knowledge Panel!",
        ],
    },
    reddit: {
        name: "Reddit",
        color: "#FF4500",
        icon: "🤖",
        setupSteps: [
            'Go to reddit.com/prefs/apps → Create "script" app',
            "Note client_id (under app name) and client_secret",
            "Get access token via password grant",
            'Store in metadata: {"client_id":"...","client_secret":"...","username":"...","default_subreddit":"speechtherapy"}',
            "60 requests/min — Google indexes Reddit heavily!",
            "Important: content must be genuinely helpful, not promotional",
        ],
    },
    medium: {
        name: "Medium",
        color: "#000000",
        icon: "✍️",
        setupSteps: [
            "Go to medium.com/me/settings/security → Integration tokens",
            "Generate an integration token",
            "GET /v1/me to get your authorId",
            'Store in metadata: {"author_id":"..."}',
            "Set canonical URL to your blog for SEO credit!",
        ],
    },
};

// ── API Service ──

export const socialMediaService = {
    // Dashboard
    getDashboard: () =>
        http.get<{ stats: DashboardStats; analytics: DashboardAnalytics }>(
            "/social-media/dashboard"
        ),

    getDashboardAnalytics: (days = 30) =>
        http.get<DashboardAnalytics>("/social-media/dashboard/analytics", {
            params: { days },
        }),

    // Platforms
    listPlatforms: () => http.get<SocialPlatform[]>("/social-media/platforms"),

    connectPlatform: (data: {
        platform: string;
        account_name: string;
        access_token: string;
        refresh_token?: string;
        token_expires?: string;
        platform_id?: string;
        metadata?: Record<string, any>;
    }) => http.post<SocialPlatform>("/social-media/platforms", data),

    updatePlatform: (
        id: number,
        data: Partial<{
            account_name: string;
            access_token: string;
            refresh_token: string;
            token_expires: string;
            platform_id: string;
            is_active: boolean;
            metadata: Record<string, any>;
        }>
    ) => http.patch<SocialPlatform>(`/social-media/platforms/${id}`, data),

    deletePlatform: (id: number) =>
        http.delete(`/social-media/platforms/${id}`),

    togglePlatform: (id: number, is_active: boolean) =>
        http.post(`/social-media/platforms/${id}/toggle`, { is_active }),

    validatePlatforms: () =>
        http.get<
            Array<{ platform: string; accountName: string; valid: boolean }>
        >("/social-media/platforms/validate"),

    // Campaigns
    listCampaigns: (filters?: {
        status?: string;
        limit?: number;
        offset?: number;
    }) =>
        http.get<{ campaigns: ContentCampaign[]; total: number }>(
            "/social-media/campaigns",
            { params: filters }
        ),

    getCampaign: (id: number) =>
        http.get<ContentCampaign>(`/social-media/campaigns/${id}`),

    createFromBlog: (data: {
        articleId: number;
        targetPlatforms?: string[];
        autoApprove?: boolean;
    }) =>
        http.post<ContentCampaign>("/social-media/campaigns/from-blog", data),

    createFromPrompt: (data: {
        title: string;
        prompt: string;
        targetPlatforms: string[];
        autoApprove?: boolean;
    }) =>
        http.post<ContentCampaign>("/social-media/campaigns/from-prompt", data),

    approveCampaign: (id: number) =>
        http.post<ContentCampaign>(`/social-media/campaigns/${id}/approve`),

    dispatchCampaign: (id: number) =>
        http.post<{ total: number; success: number; failed: number }>(
            `/social-media/campaigns/${id}/dispatch`
        ),

    deleteCampaign: (id: number) =>
        http.delete(`/social-media/campaigns/${id}`),

    getCampaignAnalytics: (id: number) =>
        http.get(`/social-media/campaigns/${id}/analytics`),

    // Posts
    updatePost: (
        id: number,
        data: { content?: string; status?: string; scheduled_at?: string }
    ) => http.patch<SocialPost>(`/social-media/posts/${id}`, data),

    dispatchPost: (id: number) =>
        http.post<{ success: boolean; error?: string }>(
            `/social-media/posts/${id}/dispatch`
        ),

    // AI Generation
    generateFromBlog: (articleId: number, targetPlatforms?: string[]) =>
        http.post<ContentBundle>("/social-media/generate/from-blog", {
            articleId,
            targetPlatforms,
        }),

    generateFromPrompt: (prompt: string, platforms: string[]) =>
        http.post<ContentBundle>("/social-media/generate/from-prompt", {
            prompt,
            platforms,
        }),

    // Analytics & Calendar
    getAnalytics: (days = 30) =>
        http.get<DashboardAnalytics>("/social-media/analytics", {
            params: { days },
        }),

    pullAnalytics: () => http.post("/social-media/analytics/pull"),

    getCalendar: (start: string, end: string) =>
        http.get("/social-media/calendar", { params: { start, end } }),

    getUpcoming: (limit = 20) =>
        http.get("/social-media/upcoming", { params: { limit } }),
};
